"use client";
import { SchemaMarkup } from '../components/SEO/SchemaMarkup';
import { ENTITY } from '../data/entityData';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, MapPin, Clock, MessageCircle, Check } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import GeometricPattern from '../components/GeometricPattern';
import CrescentMoon from '../components/CrescentMoon';
import { COMPANY, PACKAGES } from '../data/mock';
import { brandConfig } from '../brandConfig';
import { sacredRise, goldLine } from '../lib/animations';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December', 'Ramadan', 'Hajj Season'];
const TRAVELERS = ['1 Person', '2 People', '3 People', '4 People', '5 People', '6-9 People', '10+ People'];

export default function Contact() {
  const [form, setForm] = useState({ name: '', arabicName: '', phone: '', package: '', travelers: '', month: '', notes: '' });
  const [submitted, setSubmitted] = useState(false);
  const [ref, setRef] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const ref = 'HJR-' + Math.floor(100000 + Math.random() * 900000);
    
    // Save to Firebase using the global function from index.html
    if (window.submitToFirebase) {
      await window.submitToFirebase(form);
    }

    // Keep existing localStorage logic as backup
    const existing = JSON.parse(localStorage.getItem('hijra_bookings') || '[]');
    existing.push({ ...form, ref, createdAt: new Date().toISOString() });
    localStorage.setItem('hijra_bookings', JSON.stringify(existing));
    
    setRef(ref);
    setSubmitted(true);
    
    // Clear form fields
    setForm({ name: '', arabicName: '', phone: '', package: '', travelers: '', month: '', notes: '' });
  };

  const update = (k, v) => setForm({ ...form, [k]: v });

  return (
    <PageWrapper>
      <SchemaMarkup
        pageTitle={`Contact Us | ${brandConfig.companyName}`}
        pageDescription={`Get in touch with ${brandConfig.companyName}. Available 24/7 for your Umrah bookings, Visa inquiries, and urgent travel needs in Addis Ababa.`}
        pageUrl="/contact"
      />
      <section style={{ position: 'relative', minHeight: '45vh', overflow: 'hidden', background: '#0A1628', display: 'flex', alignItems: 'center' }}>
        <img src="https://images.unsplash.com/photo-1592326871020-04f58c1a52f3?w=1920&h=600&fit=crop&q=80" alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,22,40,0.85)' }} />
        <GeometricPattern color="#C9A84C" opacity={0.05} size={100} />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 'var(--container)', margin: '0 auto', padding: '120px var(--gutter) 60px' }}>
          <div className="arabic-text" style={{ fontSize: 18, color: '#C9A84C' }}>{COMPANY.bismillah}</div>
          <h1 style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#FFFFFF', fontSize: 'var(--h1)', marginTop: 16, lineHeight: 1.1 }}>
            Begin Your<br /><span style={{ color: '#C9A84C' }}>Sacred Journey.</span>
          </h1>
          <p style={{ fontFamily: 'Inter', color: '#FBF8F0', fontSize: 'var(--body-lg)', maxWidth: 560, marginTop: 16 }}>
            We are available 24 hours, every day. Let us take care of the arrangements.
          </p>
        </div>
      </section>

      <section style={{ background: '#FBF8F0', padding: 'var(--section) 0' }}>
        <div className="contact-grid" style={{
          maxWidth: 'var(--container)', margin: '0 auto', padding: '0 var(--gutter)',
          display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 60,
        }}>
          {/* LEFT INFO */}
          <div>
            <div style={{ borderLeft: '4px solid #C9A84C', paddingLeft: 20, marginBottom: 36 }}>
              <div style={{ fontFamily: 'Inter', fontWeight: 500, color: '#0A1628', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
                Call or WhatsApp
              </div>
              <a href={`tel:${COMPANY.phoneIntl}`} style={{ textDecoration: 'none' }}>
                <div style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#0A1628', fontSize: 38, marginTop: 4 }}>
                  {COMPANY.phoneDisplay}
                </div>
              </a>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0A1628', fontSize: 14, marginTop: 10 }}>
                <Clock size={14} color="#1B6B3A" /> Open 24 hours · 7 days a week
              </div>
              <div className="arabic-text" style={{ fontStyle: 'italic', color: '#C9A84C', fontSize: 14, marginTop: 4 }}>
                مفتوحون على مدار الساعة
              </div>
            </div>

            <div style={{ borderLeft: '4px solid #1B6B3A', paddingLeft: 20, marginBottom: 36 }}>
              <div style={{ fontFamily: 'Inter', fontWeight: 500, color: '#0A1628', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
                Email
              </div>
              <a href={`mailto:${brandConfig.contactEmail}`} style={{ textDecoration: 'none' }}>
                <div style={{ fontFamily: 'Inter', fontWeight: 600, color: '#0A1628', fontSize: 18, marginTop: 8, wordBreak: 'break-all' }}>
                  {brandConfig.contactEmail}
                </div>
              </a>
            </div>

            <div style={{ borderLeft: '4px solid #C9A84C', paddingLeft: 20, marginBottom: 36 }}>
              <div style={{ fontFamily: 'Inter', fontWeight: 500, color: '#0A1628', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
                Social Media
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
                <a href={brandConfig.facebookLink} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: '50%', background: '#F5EFE0', color: '#1B6B3A', textDecoration: 'none', transition: 'all 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.background = '#1B6B3A'; e.currentTarget.style.color = '#FBF8F0'; }} onMouseLeave={(e) => { e.currentTarget.style.background = '#F5EFE0'; e.currentTarget.style.color = '#1B6B3A'; }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3.65l.35-4H14V7a1 1 0 0 1 1-1h3z"></path>
                  </svg>
                </a>
                <a href={brandConfig.instagramLink} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: '50%', background: '#F5EFE0', color: '#1B6B3A', textDecoration: 'none', transition: 'all 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.background = '#1B6B3A'; e.currentTarget.style.color = '#FBF8F0'; }} onMouseLeave={(e) => { e.currentTarget.style.background = '#F5EFE0'; e.currentTarget.style.color = '#1B6B3A'; }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </a>
                <a href={COMPANY.social.tiktok} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: '50%', background: '#F5EFE0', color: '#1B6B3A', textDecoration: 'none', transition: 'all 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.background = '#1B6B3A'; e.currentTarget.style.color = '#FBF8F0'; }} onMouseLeave={(e) => { e.currentTarget.style.background = '#F5EFE0'; e.currentTarget.style.color = '#1B6B3A'; }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5v3a3 3 0 0 1-3-3" />
                  </svg>
                </a>
              </div>
            </div>

            <div style={{ position: 'relative', overflow: 'hidden', height: 280, marginBottom: 24 }}>
              <img src="https://images.unsplash.com/photo-1564769625905-50e93615e769?w=600&h=300&fit=crop&q=80" alt="Medina Mosque" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{
                position: 'absolute', bottom: 20, left: 20, background: 'rgba(10,22,40,0.95)',
                borderLeft: '3px solid #C9A84C', padding: '14px 20px',
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}>
                <MapPin size={18} color="#C9A84C" />
                <div>
                  <div style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#FBF8F0', fontSize: 15 }}>
                    {brandConfig.companyName}
                  </div>
                  <div style={{ fontFamily: 'Inter', color: 'rgba(251,248,240,0.75)', fontSize: 12, marginTop: 2 }}>
                    {COMPANY.location}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ background: '#1B6B3A', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%', background: 'rgba(251,248,240,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <MessageCircle size={20} color="#FBF8F0" />
              </div>
              <div>
                <div style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#FBF8F0', fontSize: 15 }}>
                  Available for urgent travel needs
                </div>
                <div style={{ fontFamily: 'Inter', color: '#FBF8F0', fontSize: 12, marginTop: 4, opacity: 0.85 }}>
                  Call anytime — 24 hours a day, Inshallah
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT FORM */}
          <div style={{
            background: '#FFFFFF', border: '1px solid rgba(201,168,76,0.2)',
            borderTop: '4px solid #C9A84C', padding: '44px 40px',
          }}>
            {submitted ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <CrescentMoon size={56} color="#1B6B3A" />
                </div>
                <h2 style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#0A1628', fontSize: 28, marginTop: 16 }}>
                  Booking Request Received!
                </h2>
                <div className="arabic-text" style={{ fontStyle: 'italic', color: '#C9A84C', fontSize: 20, marginTop: 8 }}>
                  تم استلام طلب الحجز!
                </div>
                <p style={{ fontFamily: 'Inter', color: '#0A1628', fontSize: 15, marginTop: 16, lineHeight: 1.7 }}>
                  We will call you within a few hours, Inshallah.<br />
                  <span className="arabic-text" style={{ color: '#C9A84C' }}>بارك الله فيك</span>
                </p>
                <div style={{ marginTop: 20, padding: '14px 20px', background: '#F5EFE0', display: 'inline-block' }}>
                  <div style={{ fontFamily: 'Inter', fontWeight: 500, color: '#0A1628', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
                    Reference Number
                  </div>
                  <div style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#C9A84C', fontSize: 22, marginTop: 4 }}>
                    {ref}
                  </div>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={submit}>
                <h2 style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#0A1628', fontSize: 26 }}>
                  Book Your Package
                </h2>
                <div className="arabic-text" style={{ fontStyle: 'italic', color: '#C9A84C', fontSize: 16, marginTop: 4 }}>
                  احجز باقتك الآن
                </div>
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={goldLine}
                  style={{ width: 60, height: 1, background: '#C9A84C', marginTop: 16, marginBottom: 28 }} />

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <FormField label="Full Name" value={form.name} onChange={(v) => update('name', v)} placeholder="Your name" required />
                  <FormField label="Arabic Name (optional)" value={form.arabicName} onChange={(v) => update('arabicName', v)} placeholder="الاسم بالعربي" />
                </div>
                <FormField label="Phone Number" value={form.phone} onChange={(v) => update('phone', v)} placeholder="+251 9XX XXX XXXX" required />
                <FormSelect label="Package of Interest" value={form.package} onChange={(v) => update('package', v)}
                  options={PACKAGES.map(p => p.name).concat(['Other / Custom'])} required />
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <FormSelect label="Number of Travelers" value={form.travelers} onChange={(v) => update('travelers', v)} options={TRAVELERS} required />
                  <FormSelect label="Preferred Month" value={form.month} onChange={(v) => update('month', v)} options={MONTHS} required />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>Additional Notes</label>
                  <textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} rows={4}
                    placeholder="Any special requirements..." style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
                <button type="submit" className="gold-pulse" style={{
                  width: '100%', padding: 16, background: '#1B6B3A', color: '#FBF8F0',
                  border: 'none', fontFamily: 'Amiri', fontWeight: 700, fontSize: 18,
                  cursor: 'pointer', letterSpacing: '0.04em',
                }}>
                  Book Now →
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .contact-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
        }
        @media (max-width: 540px) {
          .form-row { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </PageWrapper>
  );
}

const labelStyle = {
  display: 'block', fontFamily: 'Inter', fontWeight: 600, color: '#0A1628',
  fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8,
};
const inputStyle = {
  width: '100%', background: '#FBF8F0', border: '1.5px solid rgba(201,168,76,0.3)',
  borderRadius: 10, padding: '13px 16px', fontFamily: 'Inter', fontSize: 15, color: '#0A1628',
  outline: 'none', transition: 'border 0.2s',
};

function FormField({ label, value, onChange, placeholder, required }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={labelStyle}>{label}{required && <span style={{ color: '#1B6B3A' }}> *</span>}</label>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required}
        style={inputStyle}
        onFocus={(e) => (e.target.style.borderColor = '#C9A84C')}
        onBlur={(e) => (e.target.style.borderColor = 'rgba(201,168,76,0.3)')} />
    </div>
  );
}

function FormSelect({ label, value, onChange, options, required }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={labelStyle}>{label}{required && <span style={{ color: '#1B6B3A' }}> *</span>}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} required={required} style={inputStyle}>
        <option value="">Select...</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
